import React, { lazy, Suspense, SyntheticEvent, useEffect } from 'react';
import { Backdrop, CircularProgress, Tabs, ThemeProvider } from '@mui/material';
import { TabOwnProps } from '@mui/material/Tab/Tab';

import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import { FormNames } from '@youscience/commonapp-service-common';
import { brightpathTheme } from '@youscience/theme';

import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';
import useCommonAppAdminStore from '@store/CommonAppAdminStore';

import ClonePropsComponent from '@components/ClonePropsComponent';

import { useApplicantFormsMetadata } from '@pages/StudentDetailsPage/hooks/useApplicantFormsMetadata';
import { FormAvailabilityStatus } from '@pages/StudentDetailsPage/components/common/enums';

import { FormMetadata } from '../common/interfaces';
import { getFormStatusTextAndIcon } from '../common/formUtils';
import { NOT_YET_AVAILABLE_CARD_TOOLTIP } from '../common/constants';

const SchoolReport = lazy(() => import('./SchoolReport'));
const OptionalReport = lazy(() => import('./OptionalReport'));
const MidYearReport = lazy(() => import('./MidYearReport'));
const FinalReport = lazy(() => import('./FinalReport'));
const FeeWaiver = lazy(() => import('./FeeWaiver'));
const EarlyDecisionAgreement = lazy(() => import('./EarlyDecisionAgreement'));
const CounselorRecommendation = lazy(() => import('./CounselorRecommendation'));
const TeacherEvaluation = lazy(() => import('../CommonAppTeacherEvaluation/TeacherEvaluation'));

import {
  StyledContent,
  StyledForm,
  StyledSidenav,
  StyledTab,
  StyledTabLabel,
  sxStyles,
} from './CommonAppApplicationForms.styles';

const renderTab = (index: number, form: FormMetadata, isActive: boolean, studentName: string) => {
  const renderStatus = getFormStatusTextAndIcon(form);

  const availabilityStatus = renderStatus.text.split(' / ')[1];

  const getTooltipTitle = () => {
    switch (availabilityStatus) {
      case FormAvailabilityStatus.NOT_AVAILABLE:
        return `You have already submitted the final report for ${studentName}. This form is no longer available.`;
      case FormAvailabilityStatus.NOT_YET_AVAILABLE:
        return NOT_YET_AVAILABLE_CARD_TOOLTIP[form.name];
      default:
        return '';
    }
  };

  const label = (
    <StyledTabLabel isActive={isActive}>
      {renderStatus.icon}

      <CoreBox>
        <CoreTypography sx={sxStyles.formName} noWrap variant="body2">
          {form.name}
        </CoreTypography>

        <CoreTypography sx={sxStyles.formStatus} noWrap variant="caption">
          {renderStatus.text}
        </CoreTypography>
      </CoreBox>
    </StyledTabLabel>
  );

  return (
    <ClonePropsComponent<TabOwnProps> key={index}>
      {(tabProps: TabOwnProps) => (
        <CoreTooltip title={getTooltipTitle()} disableInteractive>
          <CoreBox sx={sxStyles.tabWrap}>
            <StyledTab {...tabProps} label={label} disabled={renderStatus.disabled} />
          </CoreBox>
        </CoreTooltip>
      )}
    </ClonePropsComponent>
  );
};

export const counselorFormPages = [
  {
    name: FormNames.SECONDARY_REPORT,
    component: (
      <Suspense fallback={null}>
        <SchoolReport key={FormNames.SECONDARY_REPORT} />
      </Suspense>
    ),
  },
  {
    name: FormNames.FEE_WAIVER,
    component: (
      <Suspense fallback={null}>
        <FeeWaiver key={FormNames.FEE_WAIVER} />
      </Suspense>
    ),
  },
  {
    name: FormNames.EARLY_DECISION,
    component: <EarlyDecisionAgreement key={FormNames.EARLY_DECISION} formName={FormNames.EARLY_DECISION} />,
  },
  {
    name: FormNames.EARLY_DECISION2,
    component: <EarlyDecisionAgreement key={FormNames.EARLY_DECISION2} formName={FormNames.EARLY_DECISION2} />,
  },
  {
    name: FormNames.RECOMMENDATION,
    component: (
      <Suspense fallback={null}>
        <CounselorRecommendation key={FormNames.RECOMMENDATION} />
      </Suspense>
    ),
  },
  {
    name: FormNames.OPTIONAL_REPORT,
    component: (
      <Suspense fallback={null}>
        <OptionalReport key={FormNames.OPTIONAL_REPORT} formName={FormNames.OPTIONAL_REPORT} />
      </Suspense>
    ),
  },
  {
    name: FormNames.OPTIONAL_REPORT2,
    component: (
      <Suspense fallback={null}>
        <OptionalReport key={FormNames.OPTIONAL_REPORT2} formName={FormNames.OPTIONAL_REPORT2} />
      </Suspense>
    ),
  },
  {
    name: FormNames.MIDYEAR_REPORT,
    component: (
      <Suspense fallback={null}>
        <MidYearReport key={FormNames.MIDYEAR_REPORT} />
      </Suspense>
    ),
  },
  {
    name: FormNames.FINAL_REPORT,
    component: (
      <Suspense fallback={null}>
        <FinalReport key={FormNames.FINAL_REPORT} />
      </Suspense>
    ),
  },
];

interface CommonAppApplicationFormsProps {
  studentName: string;
}

export const CommonAppApplicationForms = (props: CommonAppApplicationFormsProps) => {
  const { studentName } = props;

  const { activeTabs, setActiveTabs, isSavingForm, isLoadingApplicant } = useRecommenderWorkspaceStore();
  const { formsMetadata } = useApplicantFormsMetadata();

  const { getRoles } = useCommonAppAdminStore((store) => store);

  const formPages: { name: string; component: JSX.Element; data: FormMetadata }[] = [];

  counselorFormPages.forEach((page) => {
    if (!formsMetadata[page.name]) {
      return null;
    } else {
      const tab = { ...page, data: formsMetadata[page.name] };
      formPages.push(tab);
    }
  });

  if (formsMetadata[FormNames.TEACHER_EVALUATION]) {
    formPages.push({
      name: FormNames.TEACHER_EVALUATION,
      data: formsMetadata[FormNames.TEACHER_EVALUATION],
      component: <Suspense fallback={null}>{<TeacherEvaluation />}</Suspense>,
    });
  }

  useEffect(() => {
    const getData = async () => {
      await getRoles();
    };

    void getData();
  }, []);

  const removeShowErrorsParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('showErrors');
    window.history.replaceState({}, '', url.toString());
  };

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    removeShowErrorsParam();

    setActiveTabs({ activeApplicationForm: newValue });
  };

  const getActiveTab = () => formPages[activeTabs.activeApplicationForm];

  const getTab = (form: FormMetadata, index: number) =>
    renderTab(index, form, form.name === getActiveTab().name, studentName);

  return (
    <ThemeProvider theme={brightpathTheme}>
      {isLoadingApplicant ? (
        <CoreBox sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-around', padding: '2rem' }}>
          <CircularProgress />
        </CoreBox>
      ) : (
        <StyledContent>
          <StyledSidenav sx={{ border: '1px solid rgba(0, 0, 0, 0.12)' }}>
            <Tabs orientation="vertical" value={activeTabs.activeApplicationForm} onChange={handleTabChange}>
              {formPages.map((page, index) => getTab(page.data, index))}
            </Tabs>
          </StyledSidenav>

          <StyledForm sx={{ position: 'relative' }}>
            <Backdrop
              open={isSavingForm}
              sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                position: 'fixed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                color: '#1976d2',
              }}
            >
              <CircularProgress />
            </Backdrop>

            {getActiveTab()?.component ?? <>No actions required yet</>}
          </StyledForm>
        </StyledContent>
      )}
    </ThemeProvider>
  );
};
